import { useId, useState } from 'react';
import type { WeeklyPlan, Day } from '../../fixtures/plan';
import { DayDetails } from './DayDetails';
import { WeekListV2 } from '../today/v2/WeekListV2';
import styles from './WeekPlan.module.css';

/** A token that is only dashes and spaces is dropped from a spoken label. */
const PLACEHOLDER_TOKEN = /^[\s–—-]*$/;

/**
 * B350 — a day's distance with its unit ("12.3 km", "5.2 mi") must stay on ONE line.
 * The seven cells are about 38 to 40 pt wide at 375 (28.6 pt of text), and at 13 px
 * "12.3 km" is 32 pt, so the unit wrapped under the figure ("12.3" over "km"). A value
 * too wide at 13 px for its unit takes a smaller, tighter, no-wrap form (see `.valueFit`
 * in the stylesheet); a value that already fit ("5.2 mi", "5 km") keeps the 13 px cell
 * exactly as drawn. The text itself, the unit included, is untouched, so the cell still
 * reads "12.3 km" to everything that reads it.
 */
const DISTANCE_VALUE = /^\d+(?:\.\d+)?\s*(km|mi)$/;
function distanceValueNeedsFit(text: string): boolean {
  const m = DISTANCE_VALUE.exec(text);
  if (!m) return false;
  return text.length > (m[1] === 'km' ? 5 : 6);
}

type WeekPlanProps = {
  plan: WeeklyPlan;
  /**
   * Phase 6c.1 — when provided, each day cell with an `id` is a tap target.
   * B309: the tap OPENS the day's details (today included) and never writes;
   * the details panel's done / not done button is the only caller of
   * `onToggle(dayId, currentStatus)`.
   *
   * Cells WITHOUT an `id` (demo / fixture days) stay inert — fixtures
   * have no plan_day row to write to.
   */
  onToggle?: (dayId: string, currentStatus: Day['completionStatus']) => Promise<unknown>;
  /**
   * W1-TODAY-WEEK — `'v2'` renders the 2.0 week list (`WeekListV2`: one measure, seven
   * rows, the future-day rule in the day panels) in place of the 7-column grid. Absent:
   * the grid exactly as before.
   */
  variant?: 'v2';
};

/**
 * 7-day mobile plan grid. One column per day, M-S-S-etc labels.
 * Today highlighted dark. Rest days dashed. Hard days show mileage
 * in --today yellow. Used only on mobile; desktop uses a vertical
 * list variant added in step 7.
 *
 * Phase 6c.1: completed days get a ✓ checkmark in the top-right corner
 * and a slight opacity reduction (status is recognisable at a glance
 * without losing readability). B309: a tap on any day with an `id` (today
 * and rest days included) selects it and opens its details below the grid;
 * marking it done / not done is a deliberate button inside the details
 * (`DayDetails`), calling `onToggle`. A rest day can still be marked done —
 * vanilla allows it (e.g., "actually went for an easy walk on my rest day").
 */
export function WeekPlan({ plan, onToggle, variant }: WeekPlanProps) {
  // B309 — the selected day's plan_day id. Tapping it again closes the panel.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const panelId = useId();
  const selectedDay = selectedId ? (plan.days.find((d) => d.id === selectedId) ?? null) : null;

  // W1-TODAY-WEEK — after the hooks above, so the hook order never depends on the variant.
  if (variant === 'v2') return <WeekListV2 plan={plan} onToggle={onToggle} />;

  return (
    <>
      {/* B309 — discoverability hint, mobile. Renders only on the real-data
          path (onToggle set). Same device-neutral line the season calendar
          uses for its day details. */}
      {onToggle && (
        <div className={styles.hint}>
          Choose a day for details
        </div>
      )}
      <div className={styles.week}>
        {plan.days.map((d) => {
        const isRest = d.rest ?? d.mileage === '—';
        const isCompleted = d.completionStatus === 'completed';
        const isSkipped = d.completionStatus === 'skipped';
        // B309 — every cell with a plan row opens its details, today
        // included. The tap never writes; completion is the details button.
        const isClickable = !!onToggle && !!d.id;
        const isSelected = isClickable && d.id === selectedId;

        const className = [
          styles.day,
          d.today ? styles.today : '',
          isRest ? styles.rest : '',
          d.hard ? styles.hard : '',
          isCompleted ? styles.completed : '',
          isSkipped ? styles.skipped : '',
          isClickable ? styles.clickable : '',
          isSelected ? styles.selected : '',
        ]
          .filter(Boolean)
          .join(' ');

        // B-19 — when a stored actual exists (flag ON), the value cell shows the
        // shared "X of Y mi" pairing; otherwise it renders `d.mileage` exactly
        // as today. The pairing string also carries into the accessible label.
        const valueText = d.actual ? d.actual.copy : d.mileage;

        // A screen reader SPEAKS this label: a comma, never a dash, and the
        // placeholder "—" of a rest day is left out. Selection is carried by
        // aria-expanded below, not by words.
        const ariaLabel = isClickable
          ? `${[d.dayShort, d.label, valueText]
              .filter((t) => !PLACEHOLDER_TOKEN.test(t))
              .join(' ')}${isCompleted ? ', completed' : ''}`
          : undefined;

        const content = (
          <>
            <div className={styles.dayShort}>{d.dayShort[0]}</div>
            <div className={styles.label}>{d.label}</div>
            <div
              className={`${styles.value}${distanceValueNeedsFit(valueText) ? ` ${styles.valueFit}` : ''} serif tnum`}
            >
              {valueText}
            </div>
            {isCompleted && (
              <span className={styles.checkmark} aria-hidden="true">
                ✓
              </span>
            )}
            {/* RPE restore — read-only effort chip (vanilla rpe-badge
                parity). Display only: rows are whole <button>s, so the
                capture/recovery interaction lives on the hero. */}
            {isCompleted && typeof d.rpe === 'number' && (
              <span className={styles.rpeChip} aria-label={`Effort ${d.rpe} of 10`}>
                {d.rpe}
              </span>
            )}
          </>
        );

        if (isClickable) {
          return (
            <button
              key={d.dayShort}
              type="button"
              className={className}
              onClick={() => setSelectedId((prev) => (prev === d.id ? null : d.id!))}
              aria-label={ariaLabel}
              aria-expanded={isSelected}
              aria-controls={isSelected ? panelId : undefined}
            >
              {content}
            </button>
          );
        }
        return (
          <div key={d.dayShort} className={className}>
            {content}
          </div>
        );
      })}
      </div>
      {selectedDay && (
        <div className={styles.details}>
          <DayDetails key={selectedDay.id} day={selectedDay} onToggle={onToggle} id={panelId} />
        </div>
      )}
    </>
  );
}
